import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, Link2, Loader2, Paperclip, ShieldCheck, Trash2, Upload } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { linkProblem } from "../lib/portalKinds";
import { ACCEPT_ATTR, fileProblem, formatBytes } from "../lib/portalFiles";
import { uploadErrorText, uploadFile } from "../lib/portalUpload";
import { calendarRows, dueSentence, headline, slotSummary, timeLeft, whenLabel } from "../lib/portalPortfolio";
import { Chip, Shell, fieldCls, primaryBtn, secondaryBtn } from "../components/portal/PortalShell";
import { downloadFile } from "../components/portal/SubmissionHistory";

/**
 * `/portal/portofoliu/:groupId` — the participant's final portfolio: a place
 * for each component the strategy asks for, the AI tools used, and handing it
 * in, signed. The deadline and the right to hand in (from Anexa 5) are the
 * server's; this shows them and refuses nothing on its own.
 */
export default function PortalPortfolio() {
  const { groupId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/portal/groups/${groupId}/portfolio`)).data);
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am găsit portofoliul."));
    }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Shell wide>
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la portal
      </Link>
      {error && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!data && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {data && <Portfolio data={data} groupId={groupId} onChange={setData} reload={load} />}
    </Shell>
  );
}

function Portfolio({ data, groupId, onChange, reload }) {
  const s = data.session;
  const editable = !data.closed && !data.read_only && !["refused", "abandon"].includes(data.right.state);
  return (
    <div className="grid gap-8" data-testid="portal-portfolio">
      <header className="mt-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
          Portofoliul final · {data.group.name}{data.attempt > 1 ? " · retransmitere" : ""}
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#373A45]">Portofoliul de evaluare finală</h1>
        {s && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#373A45]">
            <Clock size={15} className="text-[#965EA7]" /> Se depune {dueSentence(s)}
            {!data.closed && <Chip tone="info">{timeLeft(s.portfolio_due)}</Chip>}
          </p>
        )}
        <p className={`mt-3 rounded-xl px-4 py-3 text-sm ${data.seal ? "bg-[#519872]/10 text-[#3c7457]" : "bg-white text-[#373A45] ring-1 ring-[#E5E7EB]"}`}
          data-testid="portal-portfolio-headline">
          {data.seal && <CheckCircle2 size={15} className="mr-1.5 inline -translate-y-px" />}
          {headline(data)}
          {data.seal && <span className="block text-xs">Codul de verificare al depunerii: <strong className="font-mono">{data.seal.code}</strong></span>}
        </p>
      </header>

      {(data.structure || data.resubmit?.can_request || data.resubmit?.status) && (
        <StructureResult data={data} groupId={groupId} onChange={onChange} />
      )}

      <section aria-labelledby="pf-slots">
        <h2 id="pf-slots" className="font-display text-xl font-bold text-[#373A45]">Ce conține</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Strategia de evaluare a programului cere cinci componente, iar „Alte materiale” e opțional. Pune fiecare la locul ei: așa vezi singur ce lipsește,
          iar formatorul verifică structura dintr-o privire.
        </p>
        <ul className="mt-3 grid gap-3">
          {data.slots.map((slot) => (
            <Slot key={slot.key} slot={slot} groupId={groupId} editable={editable} limits={data.limits}
              onChange={onChange} reload={reload} />
          ))}
        </ul>
      </section>

      <AiTools data={data} groupId={groupId} editable={editable} onChange={onChange} />

      {editable && <Submit data={data} groupId={groupId} onChange={onChange} />}

      {s && (
        <details className="rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm">
          <summary className="cursor-pointer font-semibold text-[#373A45]">Ce urmează după depunere</summary>
          <ul className="mt-2 grid gap-1.5">
            {calendarRows(s).map((r) => (
              <li key={r.key} className="text-[#373A45]">
                <strong>{r.when}</strong> · {r.what} <span className="text-xs text-[#6B7280]">({r.who}, procedura, {r.rule})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Slot({ slot, groupId, editable, limits, onChange, reload }) {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);
  const path = `/portal/groups/${groupId}/portfolio/slots/${slot.key}`;
  const empty = !(slot.files.length || slot.links.length);

  const save = async (files, links) => {
    setBusy(true);
    try {
      onChange((await api.put(path, { files, links })).data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file) => {
    if (!file) return;
    const problem = fileProblem(file, { limitBytes: Infinity });
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      await uploadFile(`${path}/uploads`, file);
      await reload();
      toast.success("Fișierul e încărcat.");
    } catch (err) {
      toast.error(uploadErrorText(err, apiErrorMessage));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const addLink = () => {
    const problem = linkProblem(url);
    if (problem) { toast.error(problem); return; }
    save(slot.files.map((f) => f.id), [...slot.links, { url: url.trim(), label: label.trim() }]);
    setUrl(""); setLabel("");
  };

  return (
    <li className="rounded-2xl border border-[#E5E7EB] bg-white p-4" data-testid={`portal-slot-${slot.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#373A45]">{slot.label}{slot.required ? "" : <span className="font-normal text-[#6B7280]"> (opțional)</span>}</p>
          <p className="mt-0.5 text-xs text-[#6B7280]">{slot.hint}</p>
        </div>
        <Chip tone={empty ? (slot.required ? "late" : "neutral") : "good"}>{slotSummary(slot)}</Chip>
      </div>
      {(slot.files.length > 0 || slot.links.length > 0) && (
        <ul className="mt-3 grid gap-1.5 text-sm">
          {slot.files.map((f) => (
            <li key={f.id} className="flex items-center gap-2">
              <button type="button" className="inline-flex min-w-0 items-center gap-1.5 text-left text-[#373A45] hover:text-[#965EA7]"
                onClick={() => downloadFile(f.id, (m) => toast.error(m))}>
                <Paperclip size={14} className="shrink-0" /> <span className="truncate">{f.name}</span>
                <span className="shrink-0 text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
              </button>
              {editable && (
                <button type="button" aria-label={`Scoate ${f.name}`} disabled={busy}
                  className="rounded-full p-1 text-[#9b1f2c] hover:bg-[#FDECEC]"
                  onClick={() => save(slot.files.filter((x) => x.id !== f.id).map((x) => x.id), slot.links)}>
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
          {slot.links.map((ln, i) => (
            <li key={`${ln.url}-${i}`} className="flex items-center gap-2">
              <a href={ln.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-w-0 items-center gap-1.5 text-[#965EA7] hover:underline">
                <Link2 size={14} className="shrink-0" /> <span className="truncate">{ln.label || ln.url}</span>
              </a>
              {editable && (
                <button type="button" aria-label="Scoate linkul" disabled={busy}
                  className="rounded-full p-1 text-[#9b1f2c] hover:bg-[#FDECEC]"
                  onClick={() => save(slot.files.map((x) => x.id), slot.links.filter((_, k) => k !== i))}>
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="mt-3 grid gap-2">
          <div>
            <input ref={fileRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <button type="button" className={secondaryBtn} disabled={busy || slot.files.length >= limits.max_files_per_slot}
              onClick={() => fileRef.current?.click()} data-testid={`portal-slot-upload-${slot.key}`}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Încarcă un fișier
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_12rem_auto]">
            <input className={fieldCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (opțional, un link)"
              aria-label={`Link pentru ${slot.label}`} />
            <input className={fieldCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ce e (opțional)"
              aria-label="Descrierea linkului" />
            <button type="button" className={secondaryBtn} disabled={busy || !url.trim()} onClick={addLink}>Adaugă</button>
          </div>
          {slot.key === "dissemination" && (
            <p className="text-xs text-[#6B7280]">Un document (proces-verbal, adeverință, captură de ecran) e mai sigur decât un link, care se poate schimba.</p>
          )}
        </div>
      )}
    </li>
  );
}

function AiTools({ data, groupId, editable, onChange }) {
  const [text, setText] = useState(data.ai_tools || "");
  const [busy, setBusy] = useState(false);
  const dirty = text !== (data.ai_tools || "");
  const save = async () => {
    setBusy(true);
    try {
      onChange((await api.put(`/portal/groups/${groupId}/portfolio/ai-tools`, { text })).data);
      toast.success("Am salvat instrumentele AI.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="pf-ai">
      <h2 id="pf-ai" className="font-display text-xl font-bold text-[#373A45]">Instrumentele AI folosite</h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Strategia cere să menționezi explicit instrumentele AI folosite: care, și la ce anume din portofoliu.
      </p>
      <textarea className={`${fieldCls} mt-3`} rows={3} value={text} disabled={!editable} maxLength={1000}
        onChange={(e) => setText(e.target.value)} data-testid="portal-portfolio-ai-tools"
        placeholder="de exemplu: ChatGPT pentru fișa de lucru din activitatea 1, Gemini pentru imaginile din resursă" />
      {editable && (
        <button type="button" className={`${secondaryBtn} mt-2`} disabled={busy || !dirty} onClick={save}>
          {busy && <Loader2 size={14} className="animate-spin" />} Salvează
        </button>
      )}
    </section>
  );
}

function Submit({ data, groupId, onChange }) {
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = data.right.can_submit && data.missing.length === 0;
  const submit = async () => {
    setBusy(true);
    try {
      onChange((await api.post(`/portal/groups/${groupId}/portfolio/submit`, { full_name: name, accept })).data);
      toast.success("Portofoliul e depus și semnat.");
      setAccept(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut depune portofoliul."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="pf-submit" className="rounded-2xl border border-[#965EA7]/30 bg-white p-5">
      <h2 id="pf-submit" className="flex items-center gap-2 font-display text-xl font-bold text-[#373A45]">
        <ShieldCheck size={18} className="text-[#965EA7]" /> {data.seal ? "Depune din nou" : "Semnează și depune"}
      </h2>
      <p className="mt-1 text-sm text-[#6B7280]">
        Portofoliul se depune asumat prin semnătură (procedura, art. 20 alin. 3). Semnătura ta e numele scris mai jos,
        împreună cu declarația; păstrăm ora depunerii, amprenta fiecărui fișier, adresa IP și browserul, ca dovadă.
        {data.seal ? " Ultima depunere rămâne valabilă până depui alta." : " Până la termen poți depune din nou oricând."}
      </p>
      <p className="mt-3 rounded-xl bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45]">{data.declaration.text}</p>
      <label className="mt-3 flex items-start gap-2 text-sm text-[#373A45]">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#965EA7]"
          data-testid="portal-portfolio-accept" />
        Am citit declarația și o asum.
      </label>
      <label className="mt-3 block text-sm text-[#373A45]">
        Numele tău complet, ca în înscriere
        <input className={`${fieldCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)}
          placeholder={data.official_name} data-testid="portal-portfolio-name" autoComplete="name" />
      </label>
      {!ready && (
        <p className="mt-2 text-xs text-[#8a5d12]">{data.right.can_submit ? `Mai lipsește: ${data.missing.join(", ")}.` : data.right.message}</p>
      )}
      <button type="button" className={`${primaryBtn} mt-4`} disabled={busy || !ready || !accept || !name.trim()}
        onClick={submit} data-testid="portal-portfolio-submit">
        {busy && <Loader2 size={16} className="animate-spin" />} {data.seal ? "Semnează și depune din nou" : "Semnează și depune"}
      </button>
      {data.seal && (
        <p className="mt-3 text-xs text-[#6B7280]">
          Ultima depunere: {whenLabel(data.seal.at)}, semnată de {data.seal.signature.full_name}, cod {data.seal.code}.
        </p>
      )}
    </section>
  );
}

function StructureResult({ data, groupId, onChange }) {
  const [busy, setBusy] = useState(false);
  const ok = data.structure?.verdict === "admis";
  const ask = async () => {
    setBusy(true);
    try {
      onChange((await api.post(`/portal/groups/${groupId}/portfolio/resubmit-request`)).data);
      toast.success("Cererea e trimisă. Echipa îți confirmă noul termen.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite cererea."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={`rounded-2xl p-5 ${ok ? "bg-[#519872]/10" : "bg-[#c0483f]/[0.06]"}`} data-testid="portal-portfolio-structure">
      <p className="font-semibold text-[#373A45]">
        {data.structure
          ? `Verificarea structurii: ${ok ? "admis pentru evaluarea finală" : "respins"}`
          : data.resubmit?.status === "approved" ? "Retransmiterea e aprobată: ai termenele sesiunii de restanță."
            : "Portofoliul nu a fost transmis în termen."}
      </p>
      {data.structure?.note && <p className="mt-1 text-sm text-[#373A45]">{data.structure.note}</p>}
      {!ok && data.resubmit?.can_request && (
        <>
          <p className="mt-2 text-sm text-[#6B7280]">
            Poți cere, o singură dată, să completezi și să retransmiți portofoliul. Treci în sesiunea de restanță, cu
            termene noi (procedura, art. 27 și 29).
          </p>
          <button type="button" className={`${secondaryBtn} mt-3`} disabled={busy} onClick={ask} data-testid="portal-portfolio-resubmit">
            Cer retransmiterea
          </button>
        </>
      )}
      {data.resubmit?.status === "requested" && <p className="mt-2 text-sm text-[#6B7280]">Ai cerut retransmiterea; așteaptă confirmarea echipei.</p>}
    </section>
  );
}
